import React from 'react'
import { useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui/Button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card'
import { useJourneyStore } from '@/stores/journeyStore'
import { usePresentationStore } from '@/stores/presentationStore'
import { 
  Play, 
  Clock, 
  Target, 
  TrendingUp,
  CheckCircle,
  ArrowRight,
  Users,
  Lightbulb,
  Presentation,
  UserPlus,
  Zap,
  ExternalLink,
  BookOpen
} from 'lucide-react'
import { safeToLocaleDateString } from '@/lib/utils'

export function DashboardPage() {
  const navigate = useNavigate()
  const { user, stages, currentStage, userProgress, setCurrentStage } = useJourneyStore()
  const { isPresenterMode, isParticipantMode, currentSession } = usePresentationStore()
  
  const completedStages = stages.filter(stage => stage.isCompleted).length
  const currentStageData = stages.find(stage => stage.id === currentStage)
  const nextStage = stages.find(stage => stage.id === currentStage + 1)
  
  const totalStages = stages.length
  const remainingStages = stages.filter(stage => !stage.isCompleted).length

  const handleContinueJourney = () => {
    navigate(`/stage/${currentStage}`)
  }

  const handleStartStage = (stageId: number) => {
    setCurrentStage(stageId)
    navigate(`/stage/${stageId}`)
  }

  if (!user || !currentStageData) {
    return <div>Loading...</div>
  }

  return (
    <div className="space-y-8">
      {/* Hero Section with Primary Action */}
      <div className="text-center space-y-6">
        <div className="space-y-2">
          <h1 className="text-4xl font-bold text-foreground">
            Ready to Continue Your AI Journey?
          </h1>
          <p className="text-lg text-muted-foreground">
            {currentStageData?.title} • Stage {currentStage} of {totalStages}
          </p>
        </div>
        
        {/* Primary Call-to-Action */}
        <div className="max-w-md mx-auto">
          <Card className="border-primary/20 bg-primary/5">
            <CardContent className="p-6">
              <div className="space-y-4">
                <div className="text-center">
                  <h3 className="font-semibold text-lg mb-2">{currentStageData.title}</h3>
                  <p className="text-sm text-muted-foreground">{currentStageData.description}</p>
                </div>
                <Button onClick={handleContinueJourney} size="lg" className="w-full gap-2">
                  Continue Journey <ArrowRight className="w-4 h-4" />
                </Button>
                {nextStage && (
                  <p className="text-xs text-muted-foreground text-center">
                    Next: {nextStage.title}
                  </p>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Compact Progress Stats */}
      <div className="grid md:grid-cols-3 gap-4 max-w-2xl mx-auto">
        <Card>
          <CardContent className="flex items-center p-4">
            <div className="p-2 bg-primary/10 rounded-full mr-3">
              <CheckCircle className="w-5 h-5 text-primary" />
            </div>
            <div>
              <div className="text-xl font-bold">{completedStages}</div>
              <div className="text-xs text-muted-foreground">Complete</div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="flex items-center p-4">
            <div className="p-2 bg-blue-100 dark:bg-blue-900/20 rounded-full mr-3">
              <Clock className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <div className="text-xl font-bold">{remainingStages}</div>
              <div className="text-xs text-muted-foreground">Remaining</div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="flex items-center p-4">
            <div className="p-2 bg-purple-100 dark:bg-purple-900/20 rounded-full mr-3">
              <TrendingUp className="w-5 h-5 text-purple-600 dark:text-purple-400" />
            </div>
            <div>
              <div className="text-xl font-bold">{Math.round((completedStages / stages.length) * 100)}%</div>
              <div className="text-xs text-muted-foreground">Progress</div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Collapsible Journey Overview */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Lightbulb className="w-5 h-5 text-yellow-600" />
            Journey Overview
          </CardTitle>
          <CardDescription>
            View all stages and track your progress
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {stages.map((stage) => (
              <div
                key={stage.id}
                className={`flex items-center justify-between p-3 rounded-lg border transition-all cursor-pointer hover-lift ${
                  stage.isCompleted 
                    ? 'bg-green-50 border-green-200 dark:bg-green-900/20 dark:border-green-800'
                    : stage.id === currentStage
                    ? 'bg-primary/5 border-primary/20'
                    : stage.isLocked
                    ? 'bg-muted/50 border-muted opacity-60 cursor-not-allowed'
                    : 'bg-card border-border hover:border-primary/50'
                }`}
                onClick={() => !stage.isLocked && handleStartStage(stage.id)}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                    stage.isCompleted 
                      ? 'bg-green-500 text-white'
                      : stage.id === currentStage
                      ? 'bg-primary text-primary-foreground'
                      : stage.isLocked
                      ? 'bg-muted text-muted-foreground'
                      : 'bg-background border text-foreground'
                  }`}>
                    {stage.isCompleted ? (
                      <CheckCircle className="w-4 h-4" />
                    ) : (
                      stage.id
                    )}
                  </div>
                  <div>
                    <h3 className="font-medium text-sm">{stage.title}</h3>
                    <p className="text-xs text-muted-foreground">{stage.subtitle}</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-2">
                  <span className="text-xs text-muted-foreground">~{stage.estimatedTime} min</span>
                  {stage.id === currentStage && (
                    <div className="px-2 py-1 bg-primary/10 text-primary text-xs rounded-md">
                      Current
                    </div>
                  )}
                  {stage.isLocked && !stage.isCompleted && (
                    <div className="px-2 py-1 bg-muted text-muted-foreground text-xs rounded-md">
                      Locked
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* AI Tools Reference Section */}
      <Card className="border-primary/20 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Zap className="w-5 h-5 text-primary" />
            AI Tools Reference Guide
          </CardTitle>
          <CardDescription>
            Comprehensive directory of AI tools organized by implementation complexity
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <h3 className="font-medium">What You'll Find</h3>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-600" />
                  <span>25+ AI tools categorized by Crawl-Walk-Run framework</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-600" />
                  <span>Implementation difficulty and time estimates</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-600" />
                  <span>Cost breakdowns and ROI expectations</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-600" />
                  <span>Getting started guides for each tool</span>
                </li>
              </ul>
            </div>
            
            <div className="space-y-4">
              <h3 className="font-medium">Tool Categories</h3>
              <div className="grid grid-cols-3 gap-3 text-center">
                <div className="p-3 bg-green-100 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
                  <div className="text-sm font-medium text-green-700 dark:text-green-400">CRAWL</div>
                  <div className="text-xs text-green-600 dark:text-green-300">Quick wins</div>
                </div>
                <div className="p-3 bg-blue-100 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                  <div className="text-sm font-medium text-blue-700 dark:text-blue-400">WALK</div>
                  <div className="text-xs text-blue-600 dark:text-blue-300">Automation</div>
                </div>
                <div className="p-3 bg-purple-100 dark:bg-purple-900/20 rounded-lg border border-purple-200 dark:border-purple-800">
                  <div className="text-sm font-medium text-purple-700 dark:text-purple-400">RUN</div>
                  <div className="text-xs text-purple-600 dark:text-purple-300">Advanced AI</div>
                </div>
              </div>
              
              <div className="flex gap-3 pt-2">
                <Button 
                  onClick={() => navigate('/ai-tools-reference')} 
                  className="flex-1 gap-2"
                >
                  <BookOpen className="w-4 h-4" />
                  Browse Tools
                </Button>
                <Button 
                  onClick={() => navigate('/overview')} 
                  variant="outline" 
                  className="gap-2"
                >
                  <ExternalLink className="w-4 h-4" />
                  AI Overview
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Bottom Section - Secondary Information */}
      <div className="grid lg:grid-cols-2 gap-8">
        {/* User Profile */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="w-5 h-5 text-blue-600" />
              Your Profile
            </CardTitle>
            <CardDescription>
              Journey personalization
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex justify-between">
              <span className="text-sm text-muted-foreground">Business Type:</span>
              <span className="text-sm font-medium">{user.businessType}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-muted-foreground">Industry:</span>
              <span className="text-sm font-medium">{user.industry}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-muted-foreground">AI Experience:</span>
              <span className="text-sm font-medium capitalize">{user.currentAIExperience}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-muted-foreground">Member Since:</span>
              <span className="text-sm font-medium">
                {safeToLocaleDateString(user.createdAt)}
              </span>
            </div>
          </CardContent>
        </Card>

        {/* Simple Presentation Mode */}
        {!isPresenterMode && !isParticipantMode && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Presentation className="w-5 h-5 text-purple-600" />
                Presentation Mode
              </CardTitle>
              <CardDescription>
                Present the AI Journey to your audience
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="p-4 border rounded-lg space-y-3">
                <div className="flex items-center gap-2">
                  <Presentation className="w-5 h-5 text-green-600" />
                  <h3 className="font-medium">Start Presentation</h3>
                </div>
                <p className="text-sm text-muted-foreground">
                  View the AI overview presentation - perfect for screen sharing
                </p>
                <Button onClick={() => navigate('/overview')} className="w-full gap-2">
                  <Presentation className="w-4 h-4" />
                  Open Presentation
                </Button>
              </div>

              <div className="p-4 border rounded-lg space-y-3">
                <div className="flex items-center gap-2">
                  <Target className="w-5 h-5 text-blue-600" />
                  <h3 className="font-medium">Demo Individual Stages</h3>
                </div>
                <p className="text-sm text-muted-foreground">
                  Jump directly to any stage for live demonstration
                </p>
                <div className="grid grid-cols-3 gap-2">
                  {stages.slice(1).map((stage) => (
                    <Button 
                      key={stage.id}
                      onClick={() => navigate(`/stage/${stage.id}`)}
                      variant="outline" 
                      size="sm"
                      className="text-xs"
                    >
                      Stage {stage.id}
                    </Button>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Presentation Session Info */}
      {(isPresenterMode || isParticipantMode) && currentSession && (
        <Card className="border-primary/20 bg-primary/5">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              {isPresenterMode ? (
                <><Presentation className="w-5 h-5 text-green-600" />Presenter Mode</>
              ) : (
                <><Users className="w-5 h-5 text-blue-600" />Participant Mode</>
              )}
            </CardTitle>
            <CardDescription>
              {currentSession.title} • {currentSession.participants.length} participants
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div className="text-sm text-muted-foreground">
                Session ID: {currentSession.id}
              </div>
              {isPresenterMode && (
                <Button onClick={() => navigate('/presenter')} size="sm" className="gap-2">
                  <Presentation className="w-4 h-4" />
                  Presenter Dashboard
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  )
}